/**
 * Hierarchical Field Renderer Component (Refactored)
 *
 * Purpose: Renders hierarchical list fields using RichTreeView with Dialog editor
 * Architecture: Compact form display with Dialog containing RichTreeView for selection
 * Dependencies: React, Material-UI, Custom hooks and components
 * Author: Development Team
 * Created: 2025-02-03
 * Refactored: 2025-01-21
 *
 * Features:
 * - Compact form display with selected values as chips
 * - Dialog containing RichTreeView for hierarchy selection
 * - Search functionality (Enter key) with bold/color highlighting of matches
 * - Lazy loading of child nodes with caching
 * - Single-select mode: Only items at field's hierarchy level are selectable
 * - Multiselect mode: All hierarchy levels selectable with ancestor optimization
 *
 * Refactoring Notes:
 * - Extracted hooks: useHierarchyData, useTreeNavigation, useNodeSelection
 * - Extracted components: HierarchySelector, HierarchyDialog
 * - Extracted utilities: hierarchyHelpers.ts
 * - Original file: 1,232 lines → Refactored file: ~200 lines (84% reduction)
 */

import React, { useState, useEffect, useCallback } from 'react';
import type { FieldRendererProps } from '../metadata-form/types';
import {
  useHierarchyData,
  useTreeNavigation,
  useNodeSelection,
} from '../../hooks/useHierarchyField';
import {
  usePickerFilters,
  usePickerLockedValues,
  usePickerScope,
  usePickerTenant,
} from '../../context/PickerScopeContext';
import { HierarchySelector } from './HierarchySelector';
import { HierarchyDialog } from './HierarchyDialog';
import { useAuth } from '../../context/AuthContext';
import { HIERARCHY_CONFIG } from '../../types/hierarchy';
import { ALL_LABEL, ALL_VALUE, isAll } from '../../utils/listValues';
import { selectAllMode } from '../../utils/selectAll';
import { isNodeSelectable } from '../../utils/hierarchyHelpers';
import type { MasterDataValue } from '../../types/mdm';

/**
 * Props interface for HierarchicalFieldRenderer
 */
export interface HierarchicalFieldRendererProps extends FieldRendererProps {
  /**
   * Maximum hierarchy depth (from hierarchy_config)
   */
  maxLevels?: number;
}

/**
 * Hierarchical Field Renderer Component
 *
 * Purpose: Provides TreeView-based interface for hierarchical MDM values
 * Logic:
 * - Form display: Shows selected values as chips with "Select..." button
 * - Dialog: Contains TreeView with search, lazy loading, and selection
 * - Single-select: Only field's level has selectable checkboxes
 * - Multiselect: All levels have selectable checkboxes
 * - Efficient storage: Stores ancestors only (not all descendants)
 *
 * Input: HierarchicalFieldRendererProps
 * Output: JSX.Element
 *
 * Example:
 * ```tsx
 * <HierarchicalFieldRenderer
 *   field={field}
 *   value={value}
 *   onChange={onChange}
 *   error={error}
 * />
 * ```
 */
export const HierarchicalFieldRenderer: React.FC<HierarchicalFieldRendererProps> = ({
  field,
  value,
  onChange,
  error,
  touched,
  disabled,
  readOnly,
  maxLevels: _maxLevels = 10,
  indicatorBorderColor,
  selectAll,
}) => {
  const label = field.label_override || field.field_definition.label;
  const isMultiselect = field.field_definition.is_multiselect;
  const fieldLevel = field.field_definition.hierarchy_config?.hierarchy_level || 1;
  const { state } = useAuth();
  const pickerTenant = usePickerTenant();
  const activeTenantId = pickerTenant || state.user?.activeTenant?.tenant_id || field.tenant_id;

  // Dialog state
  const [editorOpen, setEditorOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  // ORCH-418: "All" is ticked in the dialog (Apply stores [ALL_VALUE])
  const [allSelected, setAllSelected] = useState(false);
  const valueIsAll = isMultiselect && isAll(value);

  // 1. Use hierarchy data hook
  const {
    hierarchyChain,
    maxLevel: hierarchyDepth,
    loading: hierarchyLoading,
    error: hierarchyError,
  } = useHierarchyData(field.fielddef_id, activeTenantId);

  // 2. Use tree navigation hook
  const {
    rootNodes,
    expandedNodes,
    searchTerm,
    searchMatchIds,
    loading: treeLoading,
    error: treeError,
    setExpandedNodes,
    setSearchTerm,
    setSearchMatchIds,
    loadRootNodes,
    handleNodeExpand,
    expandAll,
    handleSearch,
    resetTree,
    nodeCache,
  } = useTreeNavigation(
    hierarchyChain,
    activeTenantId,
    editorOpen,
    usePickerScope(),
    // ORCH-400: the form's related-field constraints for this picker
    usePickerFilters(field.fielddef_id)
  );
  // ORCH-400 decision 9: stored values outside this user's access can't be removed
  const locked = usePickerLockedValues(field.fielddef_id);

  // 3. Use node selection hook
  const {
    selectedNodeIds,
    selectedValues,
    setSelectedNodeIds,
    loadInitialValue: _loadInitialValue,
    handleApply: applySelection,
    handleRemove,
  } = useNodeSelection(value, isMultiselect, activeTenantId);

  /**
   * Initialize root nodes when dialog opens
   */
  useEffect(() => {
    if (editorOpen && rootNodes.length === 0 && !treeLoading && hierarchyChain.length > 0) {
      loadRootNodes().catch((err) => {
        console.error('[HierarchicalFieldRenderer] Error loading root nodes:', err);
      });
    }
    if (!editorOpen) {
      // Reset tree when dialog closes
      resetTree();
    }
  }, [editorOpen, rootNodes.length, treeLoading, hierarchyChain.length, loadRootNodes, resetTree]);

  /**
   * Initialize selected nodes when dialog opens
   */
  useEffect(() => {
    if (editorOpen && rootNodes.length > 0) {
      const selectedIds = selectedValues.map((v) => v.id);
      setSelectedNodeIds(selectedIds);

      // Expand to show selected values and load their children
      if (selectedValues.length > 0) {
        const pathsToExpand = new Set<string>();
        const hierarchyPaths: Array<Array<{ id: string; fielddef_id: string }>> = [];

        selectedValues.forEach((val) => {
          if (val.hierarchy_path && val.hierarchy_path.length > 0) {
            hierarchyPaths.push(val.hierarchy_path);
            val.hierarchy_path.forEach((item) => {
              pathsToExpand.add(item.id);
            });
          }
        });

        const expandedArray = Array.from(pathsToExpand);
        setExpandedNodes(expandedArray);

        // Load children for all expanded nodes
        const loadExpandedNodes = async () => {
          for (const path of hierarchyPaths) {
            for (let i = 0; i < path.length - 1; i++) {
              const nodeId = path[i].id;
              await handleNodeExpand(nodeId).catch((err) => {
                console.error(
                  '[HierarchicalFieldRenderer] Error loading children for node:',
                  nodeId,
                  err
                );
              });
            }
          }
        };

        // Wait a bit for root nodes to be fully rendered before loading children
        setTimeout(async () => {
          await loadExpandedNodes();

          // After loading, scroll to first selected value
          if (selectedValues.length > 0) {
            const firstSelectedName = selectedValues[0].name;
            setTimeout(() => {
              const treeViewContainer = document.getElementById('hierarchy-tree-view');
              if (treeViewContainer) {
                const treeItems = treeViewContainer.querySelectorAll('[role="treeitem"]');
                let selectedElement: HTMLElement | null = null;

                for (const item of treeItems) {
                  const content = item.querySelector('.MuiTreeItem-content') as HTMLElement;
                  if (content) {
                    const textContent = content.textContent || '';
                    if (textContent.includes(firstSelectedName)) {
                      const checkbox = content.querySelector('input[type="checkbox"]:checked');
                      if (checkbox) {
                        selectedElement = content;
                        break;
                      }
                    }
                  }
                }

                if (selectedElement) {
                  selectedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  selectedElement.style.backgroundColor = 'rgba(25, 118, 210, 0.15)';
                  setTimeout(() => {
                    selectedElement!.style.backgroundColor = '';
                  }, HIERARCHY_CONFIG.HIGHLIGHT_DURATION_MS);
                }
              }
            }, HIERARCHY_CONFIG.SCROLL_DELAY_MS);
          }
        }, HIERARCHY_CONFIG.TREE_LOAD_DELAY_MS);
      }
    }
  }, [
    editorOpen,
    selectedValues,
    rootNodes.length,
    setSelectedNodeIds,
    setExpandedNodes,
    handleNodeExpand,
  ]);

  /**
   * Handle open dialog
   */
  const handleOpenDialog = useCallback(() => {
    if (!disabled && !readOnly) {
      setAllSelected(valueIsAll);
      setEditorOpen(true);
      setIsFocused(true);
    }
  }, [disabled, readOnly, valueIsAll]);

  /**
   * Handle close dialog
   */
  const handleCloseDialog = useCallback(() => {
    setEditorOpen(false);
    setSearchTerm('');
    setSearchMatchIds([]);
    setIsFocused(false);
    // Reset to original selection
    const selectedIds = selectedValues.map((v) => v.id);
    setSelectedNodeIds(selectedIds);
  }, [selectedValues, setSelectedNodeIds, setSearchTerm, setSearchMatchIds]);

  /**
   * Handle apply selection
   */
  const handleApplySelection = useCallback(() => {
    if (isMultiselect && allSelected) {
      onChange([ALL_VALUE]);
    } else {
      applySelection(nodeCache, isMultiselect, onChange);
    }
    setEditorOpen(false);
    setSearchTerm('');
    setIsFocused(false);
  }, [applySelection, nodeCache, isMultiselect, onChange, setSearchTerm, allSelected]);

  /**
   * Handle remove value
   * Also reset focus state to prevent the control from appearing focused after chip removal
   */
  const handleRemoveValue = useCallback(
    (valueId: string) => {
      if (valueId === ALL_VALUE) {
        onChange([]);
        setIsFocused(false);
        return;
      }
      if (locked.has(valueId)) return;
      handleRemove(valueId, isMultiselect, onChange);
      setIsFocused(false);
    },
    [handleRemove, isMultiselect, onChange, locked]
  );
  const lockedNames = selectedValues.filter((v) => locked.has(v.id)).map((v) => v.name);
  const lockedHelper = lockedNames.length
    ? `${lockedNames.join(', ')} ${lockedNames.length === 1 ? 'is' : 'are'} outside your access and can't be removed.`
    : undefined;

  // ORCH-418: the "Select All" row. A scoped tree (non-pickable ancestors) stores explicit nodes
  // ("All Available"), and so does an active search ("All Matching Results").
  const narrowed = rootNodes.some((node) => !isNodeSelectable(node));
  const allMode = selectAllMode(
    isMultiselect && field.allow_select_all !== false && selectAll !== false,
    narrowed,
    searchMatchIds.length > 0
  );
  const allChipValue = { id: ALL_VALUE, name: ALL_LABEL } as MasterDataValue;

  return (
    <>
      {/* Form Display */}
      <HierarchySelector
        label={label}
        selectedValues={valueIsAll ? [allChipValue] : selectedValues}
        isRequired={field.is_required || false}
        disabled={disabled}
        readOnly={readOnly}
        touched={touched}
        error={error}
        helperText={lockedHelper || field.description_override || undefined}
        lockedIds={locked}
        isFocused={isFocused}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onClick={handleOpenDialog}
        onRemove={handleRemoveValue}
        indicatorBorderColor={indicatorBorderColor}
      />

      {/* Hierarchy Editor Dialog */}
      <HierarchyDialog
        open={editorOpen}
        label={label}
        isMultiselect={isMultiselect}
        fieldLevel={fieldLevel}
        hierarchyDepth={hierarchyDepth}
        rootNodes={rootNodes}
        expandedNodes={expandedNodes}
        selectedNodeIds={selectedNodeIds}
        searchTerm={searchTerm}
        searchMatchIds={searchMatchIds}
        loading={treeLoading || hierarchyLoading}
        error={treeError || hierarchyError}
        nodeCache={nodeCache}
        onClose={handleCloseDialog}
        onApply={handleApplySelection}
        onSearchTermChange={setSearchTerm}
        onSearch={handleSearch}
        onExpandedNodesChange={setExpandedNodes}
        onSelectedNodesChange={setSelectedNodeIds}
        onNodeExpand={handleNodeExpand}
        selectAllMode={allMode}
        allSelected={allSelected}
        onSelectAllChange={setAllSelected}
        locked={locked}
        onExpandAll={expandAll}
      />
    </>
  );
};

export default HierarchicalFieldRenderer;
